import { useEffect, useRef, useState } from 'react'
import { readPreferredMapType, storePreferredMapType } from '../preferences'
import { configureGoogleMaps, importLibrary } from './googleMapsLoader'
import type { MapViewport } from './mapViewport'

const EUROPE_CENTER = { lat: 54.5, lng: 15 }

export function DesktopOverviewMap({ initialViewport }: { initialViewport?: MapViewport }) {
  const elementRef = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const apiKey = import.meta.env.VITE_MAPS_API_KEY

  useEffect(() => {
    if (!apiKey || !elementRef.current) return
    let active = true
    let mapTypeListener: google.maps.MapsEventListener | null = null
    configureGoogleMaps(apiKey)

    void Promise.all([importLibrary('maps'), importLibrary('core')]).then(([
      { Map, MapTypeControlStyle, MapTypeId },
      { ControlPosition },
    ]) => {
      if (!active || !elementRef.current) return
      const map = new Map(elementRef.current, {
        center: initialViewport?.center ?? EUROPE_CENTER,
        zoom: initialViewport?.zoom ?? 4,
        mapTypeId: readPreferredMapType() === 'satellite' ? MapTypeId.HYBRID : MapTypeId.ROADMAP,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: [MapTypeId.ROADMAP, MapTypeId.HYBRID],
          position: ControlPosition.TOP_LEFT,
          style: MapTypeControlStyle.HORIZONTAL_BAR,
        },
        streetViewControl: true,
        fullscreenControl: true,
        gestureHandling: 'greedy',
      })
      mapTypeListener = map.addListener('maptypeid_changed', () => {
        const selectedType = map.getMapTypeId()
        if (selectedType === MapTypeId.ROADMAP || selectedType === MapTypeId.HYBRID) {
          storePreferredMapType(selectedType === MapTypeId.HYBRID ? 'satellite' : 'roadmap')
        }
      })
    }).catch(() => {
      if (active) setFailed(true)
    })

    return () => {
      active = false
      mapTypeListener?.remove()
    }
  }, [apiKey, initialViewport])

  return (
    <section className="desktop-overview" aria-label="Trip map overview" data-testid="desktop-trip-overview">
      <div className="desktop-overview__map">
        {!apiKey || failed ? (
          <div className="desktop-overview__fallback" role="img" aria-label="Map of Europe">
            <span>{failed ? 'The map could not be loaded' : 'Add a Google Maps key to enable the map'}</span>
          </div>
        ) : <div ref={elementRef} className="desktop-overview__canvas" aria-label="Map of Europe" />}
      </div>
      <div className="desktop-overview__prompt">
        <span aria-hidden="true">↖</span>
        <div><strong>Select a trip</strong><small>Choose a trip from the list to replay its route here.</small></div>
      </div>
    </section>
  )
}
